import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
  CFormFeedback,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilShieldAlt } from '@coreui/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import httpClient from 'src/api/http-client';
import endpoints from 'src/api/endpoint';

const VerifyOtp = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
  const email = location.state?.email || userDetails?.email;

  useEffect(() => {
    if (!email) {
      navigate('/forget-password');
    }
  }, [email, navigate]);

  const formik = useFormik({
    initialValues: { otp: '' },
    validationSchema: Yup.object({
      otp: Yup.string().required('OTP is required').min(6, 'Invalid OTP length'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await httpClient.post(endpoints.auth.verityotp(), {
          email: email,
          otp: values.otp,
        });
        toast.success(response?.data?.message || 'OTP verified successfully');

        navigate('/reset-password', { state: { email, otp: values.otp } });
      } catch (error) {
        error.response?.data?.message?.split(',').forEach((element) => {
          toast.error(element);
        });
      }
      setLoading(false);
    },
  });

  return (
    <div
      className="min-vh-100 d-flex flex-row align-items-center"
      style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)' }}
    >
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={7} lg={5}>
            <CCard
              className="border-0"
              style={{ borderRadius: '24px', boxShadow: '0 15px 35px rgba(0,0,0,0.08)' }}
            >
              <CCardBody className="p-5">
                <div className="d-flex justify-content-center mb-4">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 text-success"
                    style={{ width: '70px', height: '70px' }}
                  >
                    <CIcon icon={cilShieldAlt} style={{ width: '32px', height: '32px' }} />
                  </div>
                </div>

                <div className="text-center mb-4">
                  <h3 className="fw-bold mb-2 text-dark">Verify OTP</h3>
                  <p className="text-muted" style={{ fontSize: '0.95rem' }}>
                    We sent a secure 6-digit code to <br />
                    <strong className="text-dark">{email}</strong>
                  </p>
                </div>

                <CForm onSubmit={formik.handleSubmit}>
                  <CInputGroup
                    className="mb-4 shadow-sm"
                    style={{ borderRadius: '12px', overflow: 'hidden' }}
                  >
                    <CInputGroupText className="bg-white border-end-0 px-3">
                      <CIcon icon={cilShieldAlt} className="text-muted" />
                    </CInputGroupText>
                    <CFormInput
                      type="text"
                      placeholder="• • • • • •"
                      className="border-start-0 py-2 bg-white fw-bold fs-5"
                      style={{ boxShadow: 'none', letterSpacing: '4px' }}
                      invalid={!!(formik.touched.otp && formik.errors.otp)}
                      {...formik.getFieldProps('otp')}
                    />
                    {!!(formik.touched.otp && formik.errors.otp) && (
                      <CFormFeedback invalid className="px-3 pb-2 bg-white">
                        {formik.errors.otp}
                      </CFormFeedback>
                    )}
                  </CInputGroup>

                  <div className="d-grid gap-2 mt-2">
                    <CButton
                      color="primary"
                      type="submit"
                      disabled={loading}
                      className="py-2 text-white fw-semibold shadow-sm"
                      style={{ borderRadius: '12px', fontSize: '1.05rem' }}
                    >
                      {loading ? <CSpinner component="span" size="sm" className="me-2" /> : null}
                      Verify Code
                    </CButton>
                  </div>
                </CForm>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default VerifyOtp;
